import React, { useEffect, useState } from 'react';
import {
  CreateBase,
  SimpleForm,
  TextInput,
  useNotify,
  useRedirect,
  useCreate,
  required,
  useDataProvider,
} from "react-admin";
import { useFormContext } from 'react-hook-form';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";

const RoleCreateForm: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create, { isLoading }] = useCreate();

  const [menus, setMenus] = useState<any[]>([]);
  const [actions, setActions] = useState<any[]>([]);
  const [selectedActionIds, setSelectedActionIds] = useState<number[]>([]);
  const [selectedMenuIds, setSelectedMenuIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    Promise.all([
      dataProvider.getList('permissionMenus', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
      }),
      dataProvider.getList('permissionActions', {
        pagination: { page: 1, perPage: 2000 },
        sort: { field: 'id', order: 'ASC' },
        filter: {},
      }),
    ])
      .then(([menusRes, actionsRes]) => {
        console.log('[RoleCreate] Loaded menus:', menusRes.data);
        console.log('[RoleCreate] Loaded actions:', actionsRes.data);
        setMenus(menusRes.data);
        setActions(actionsRes.data);

        // 默认全选所有 action
        setSelectedActionIds(actionsRes.data.map((a: any) => a.id));

        // 找出没有 action 的菜单，默认全选
        const menusWithActions = new Set(actionsRes.data.map((a: any) => a.menuId).filter((id: any) => id != null));
        const menusWithoutActions = menusRes.data
          .filter((m: any) => !menusWithActions.has(m.id))
          .map((m: any) => m.id);
        console.log('[RoleCreate] Menus without actions (pre-selected):', menusWithoutActions);
        setSelectedMenuIds(menusWithoutActions);
      })
      .catch((err) => {
        console.error('[RoleCreate] Failed to load menus or actions:', err);
        notify('Failed to load menus or actions', { type: 'warning' });
      })
      .finally(() => setLoading(false));
  }, [dataProvider, notify]);

  if (loading) return <span>Loading...</span>;

  // 按 menuId 分组 actions
  const actionsByMenu = actions.reduce((acc: Record<string, any[]>, action: any) => {
    const key = action.menuId ?? 'none';
    acc[key] = acc[key] || [];
    acc[key].push(action);
    return acc;
  }, {});

  // 一级菜单：无 parentId；二级菜单：有 parentId，挂在对应一级下
  const sortByTitle = (a: any, b: any) =>
    String(a.title || '').localeCompare(String(b.title || ''));
  const level1Menus = menus.filter((m: any) => m.parentId == null).sort(sortByTitle);
  const childrenMap = new Map<string, any[]>();
  menus.forEach((m: any) => {
    if (m.parentId == null) return;
    const key = String(m.parentId);
    if (!childrenMap.has(key)) childrenMap.set(key, []);
    childrenMap.get(key)!.push(m);
  });
  childrenMap.forEach((arr) => arr.sort(sortByTitle));

  const toggleAction = (id: number) => {
    setSelectedActionIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  // 这个组件用于在action改变时标记表单为dirty
  const FormSyncComponent: React.FC = () => {
    const form = useFormContext();
    
    useEffect(() => {
      // 当selectedActionIds改变时，更新表单的一个字段来触发dirty状态
      if (form) {
        form.setValue('_actionIdsSync', selectedActionIds.join(','), {
          shouldDirty: true,
        });
      }
    }, [selectedActionIds, form]);

    return null;
  };

  const toggleMenuWithoutActions = (menuId: number) => {
    setSelectedMenuIds(prev =>
      prev.includes(menuId)
        ? prev.filter(x => x !== menuId)
        : [...prev, menuId]
    );
  };

  const toggleMenu = (menuActions: any[]) => {
    const ids = menuActions.map(a => a.id);
    const allSelected = ids.every(id => selectedActionIds.includes(id));

    setSelectedActionIds(prev =>
      allSelected
        ? prev.filter(id => !ids.includes(id))
        : Array.from(new Set([...prev, ...ids]))
    );
  };

  const handleSave = async (data: any) => {
    try {
      // 合并所有 menuIds：1. 从选中的 actions 推导的 menuIds 2. 直接选中的没有 action 的菜单
      const menuIdsFromActions = Array.from(
        new Set(
          actions
            .filter((a: any) => selectedActionIds.includes(Number(a.id)))
            .map((a: any) => a.menuId)
            .filter((id: any) => id != null)
        )
      );
      const allMenuIds = Array.from(new Set([...menuIdsFromActions, ...selectedMenuIds]));
      
      console.log('[RoleCreate handleSave]');
      console.log('  selectedActionIds:', selectedActionIds);
      console.log('  selectedMenuIds (menus without actions):', selectedMenuIds);
      console.log('  menuIdsFromActions (derived from selected actions):', menuIdsFromActions);
      console.log('  Final allMenuIds to save:', allMenuIds);
      
      const payload = {
        roleName: data.roleName,
        description: data.description,
        menuIds: allMenuIds,
        actionIds: selectedActionIds,
      };

      console.log('[RoleCreate] Saving payload:', payload);
      
      const result = await create('permissionRoles', { data: payload });
      notify('Role created successfully', { type: 'success' });
      
      // 等待一个短的延迟确保后端完全处理数据
      setTimeout(() => {
        redirect('list', 'permissionRoles');
      }, 500);
    } catch (error) {
      console.error('Create error:', error);
      notify('Create failed', { type: 'error' });
    }
  };

  return (
    <CreateBase resource="permissionRoles">
      <SimpleForm
        sx={{
          backgroundColor: '#fff',
          p: 3,
          maxHeight: 'calc(100vh - 180px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          '& > .MuiStack-root': {
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            flexDirection: 'column',
            alignItems: 'stretch',
          },
        }}
        onSubmit={handleSave}
      >
        <FormSyncComponent />
        <Box display="flex" flexWrap="wrap" gap={2} sx={{ flexShrink: 0 }}>
          <Box flex="1 1 48%">
            <TextInput
              source="roleName"
              label="Role Name"
              fullWidth
              validate={[required()]}
            />
          </Box>

          <Box flex="1 1 48%">
            <TextInput
              source="description"
              label="Description"
              fullWidth
            />
          </Box>
        </Box>

        <Box
          flex="1 1 100%"
          sx={{
            mt: 2,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1.5, flexShrink: 0 }}>
            Menus and Actions
          </Typography>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              py: 0.5,
              '& .MuiFormControlLabel-root': { mr: 0 },
            }}
          >
              {level1Menus.map((menu1) => {
                const level2Menus = childrenMap.get(String(menu1.id)) || [];
                const actions1 = actionsByMenu[menu1.id] || [];
                const hasLevel2WithActions = level2Menus.some(
                  (m: any) => (actionsByMenu[m.id] || []).length > 0
                );
                
                // Build all ids for this menu and its actions
                const allIds1 = actions1.map((a: any) => a.id);
                const allIds2 = level2Menus.flatMap((m: any) =>
                  (actionsByMenu[m.id] || []).map((a: any) => a.id)
                );
                const allIds = [...allIds1, ...allIds2];
                
                // Check if menu1 has any actions (directly or through children)
                const hasAnyActions = allIds.length > 0;
                const allChecked1 = hasAnyActions
                  ? allIds.every((id) => selectedActionIds.includes(id))
                  : selectedMenuIds.includes(menu1.id);

                return (
                  <Box key={menu1.id} sx={{ mb: 1.5 }}>
                    {/* 一级菜单 - show all menus regardless of actions */}
                    <Box sx={{ py: 0.25, display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={allChecked1}
                            onChange={() => {
                              if (hasAnyActions) {
                                // 有 actions：切换 actions 选中状态
                                toggleMenu(
                                  allIds.length
                                    ? actions.filter((a: any) => allIds.includes(a.id))
                                    : []
                                );
                              } else {
                                // 没有 actions：直接切换菜单选中状态
                                toggleMenuWithoutActions(menu1.id);
                              }
                            }}
                          />
                        }
                        label={
                          <Typography variant="body2" fontWeight="600">
                            {menu1.title}
                          </Typography>
                        }
                      />
                    </Box>

                    {/* 一级菜单下的 actions（若有） */}
                    {actions1.length > 0 && (
                      <Box sx={{ pl: 4, mb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {actions1.map((action: any) => (
                          <FormControlLabel
                            key={action.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={selectedActionIds.includes(action.id)}
                                onChange={() => toggleAction(action.id)}
                              />
                            }
                            label={
                              <Typography variant="body2" component="span">
                                {action.actionName}
                              </Typography>
                            }
                          />
                        ))}
                      </Box>
                    )}

                    {/* 二级菜单及各自 actions - show all level2 menus even without actions */}
                    {level2Menus.map((menu2) => {
                      const menu2Actions = actionsByMenu[menu2.id] || [];
                      const hasActions2 = menu2Actions.length > 0;
                      const allChecked2 = hasActions2
                        ? menu2Actions.every((a: any) =>
                            selectedActionIds.includes(a.id)
                          )
                        : selectedMenuIds.includes(menu2.id);
                      
                      return (
                        <Box key={menu2.id} sx={{ pl: 4, mb: 0.75 }}>
                          <Box sx={{ py: 0.25 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={allChecked2}
                                  onChange={() => {
                                    if (hasActions2) {
                                      // 有 actions：切换 actions 选中状态
                                      toggleMenu(menu2Actions);
                                    } else {
                                      // 没有 actions：直接切换菜单选中状态
                                      toggleMenuWithoutActions(menu2.id);
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography variant="body2" fontWeight="500">
                                  {menu2.title}
                                </Typography>
                              }
                            />
                          </Box>
                          {menu2Actions.length > 0 && (
                            <Box
                              sx={{
                                pl: 4,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                                alignItems: 'center',
                              }}
                            >
                              {menu2Actions.map((action: any) => (
                                <FormControlLabel
                                  key={action.id}
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedActionIds.includes(action.id)}
                                      onChange={() => toggleAction(action.id)}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" component="span">
                                      {action.actionName}
                                    </Typography>
                                  }
                                />
                              ))}
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>
      </SimpleForm>
    </CreateBase>
  );
};

export default RoleCreateForm;

const RoleCreate: React.FC = () => (
  <CreateBase resource="permissionRoles">
    <RoleCreateForm />
  </CreateBase>
);
